import React, { useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useGetTemplateQuery, useUpdateTemplateMutation } from '../../../store/templateLibrary/templateLibrary'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { TemplateEditorHeader } from './TemplateEditorHeader'
import { TemplateEditorBox } from './TemplateEditorBox'
import { Col, Row, Spinner } from 'react-bootstrap'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { TemplateDetailsBox } from './TemplateDetailsBox'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { useAccount } from '../../../common/useAccount'

export const TemplateEditor: React.FC = () => {
  const { tracked, trackingClient } = useTracking()
  const user = useAppSelector(selectAuthenticatedUser)
  const { portalRef } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const { accountId, account } = useAccount()
  const { templateId = '' } = useParams()
  const { data: templateValue } = useGetTemplateQuery({ accountId, templateId })
  const [updateTemplate, { isLoading }] = useUpdateTemplateMutation()
  const canEditTemplate =
    !account?.enterpriseConfig.lockTemplates &&
    !!templateValue &&
    !['system', 'enterprise'].includes(templateValue.scope) &&
    (templateValue.scope === 'user' || (templateValue.scope === 'account' && hasTemplateAdminScope))

  const _handleTemplateChange = async (newTemplate: string) => {
    if (!templateValue) {
      return
    }
    await updateTemplate({
      accountId,
      templateId: templateValue.id,
      request: {
        template: {
          template: newTemplate,
        },
      },
    })
  }

  const handleTemplateChange = tracked(
    _handleTemplateChange,
    {
      eventName: TrackEvent.TEMPLATE_SAVED,
    },
    { condition: !!templateValue, eventDetailsExtractor: () => getTemplateLibraryTrackingDetails(templateValue!) },
  )

  useEffect(() => {
    if (templateValue?.id && !user?.isGhosted) {
      trackingClient.track({
        eventName: TrackEvent.TEMPLATE_OPENED,
        eventDetails: getTemplateLibraryTrackingDetails(templateValue),
      })
    }
  }, [templateValue?.id, trackingClient, user?.isGhosted])

  return (
    <div className='truly-full-size-content'>
      {portalRef && portalRef.current ? createPortal(<TemplateEditorHeader templateName={templateValue?.name} />, portalRef.current) : null}
      {templateValue ? (
        <Row className='h-100'>
          <Col className='ps-3 pe-0' xs={8}>
            <TemplateEditorBox
              templateValue={templateValue}
              onSave={handleTemplateChange}
              loading={isLoading}
              shouldReset={false}
              readOnly={!canEditTemplate}
            />
          </Col>
          <Col xs={4} className='zocks-bg-color-gray-100 border-start zocks-p-2.0'>
            <TemplateDetailsBox templateValue={templateValue} readOnly={!canEditTemplate} />
          </Col>
        </Row>
      ) : (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
    </div>
  )
}
